import React from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { clsx } from 'clsx';

interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T, index: number) => string | number;
  renderMobileCard?: (item: T, index: number) => React.ReactNode;
  className?: string;
  emptyState?: React.ReactNode;
  loading?: boolean;
  loadingSkeleton?: React.ReactNode;
}

export function ResponsiveTable<T>({
  data,
  columns,
  keyExtractor,
  renderMobileCard,
  className,
  emptyState,
  loading = false,
  loadingSkeleton,
}: ResponsiveTableProps<T>) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (loading && loadingSkeleton) {
    return <>{loadingSkeleton}</>;
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className={clsx('space-y-4', className)}>
        {data.map((item, index) => {
          const key = keyExtractor(item, index);
          
          // Use custom mobile card renderer if provided
          if (renderMobileCard) {
            return (
              <div key={key} className="bg-white rounded-lg border border-neutral-200 p-4 shadow-sm">
                {renderMobileCard(item, index)}
              </div>
            );
          }

          // Default mobile card layout
          return (
            <div key={key} className="bg-white rounded-lg border border-neutral-200 p-4 shadow-sm">
              <div className="space-y-3">
                {columns.map((column) => (
                  <div key={column.key} className="flex justify-between items-start gap-4">
                    <span className="text-sm font-medium text-neutral-500 min-w-[100px]">
                      {column.header}
                    </span>
                    <div className="text-sm text-neutral-900 text-right flex-1">
                      {column.render(item)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className={clsx('overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={clsx(
                  'px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider',
                  column.headerClassName
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {data.map((item, index) => {
            const key = keyExtractor(item, index);
            return (
              <tr key={key} className="hover:bg-neutral-50 transition-colors">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx('px-6 py-4 whitespace-nowrap', column.className)}
                  >
                    {column.render(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Wrapper component with better TypeScript inference
export const createResponsiveTable = <T,>() => {
  return (props: ResponsiveTableProps<T>) => <ResponsiveTable {...props} />;
};

export default ResponsiveTable;

